import { EChartService } from './echart.service';
import { StatsCompetencyBoxplot } from '@/models/statistics/stats-competency-boxplot';
import { Competency } from '@/models/competency/competency';
import { NumberUtils } from '@/utils/number.utils';
import i18n from '@/i18n';

interface TotalCompetencies {
  [key: string]: CompetencyWithScores;
}

interface CompetencyWithScores {
  id: number;
  name: string;
  type: number;
  scores: number[];
}

export class MvplotCompetencyMappingEchartService extends EChartService {
  competencies: Competency[];
  competencyIDs: number[];
  self: any;
  val: number;
  prefix: string = 'competency_';
  totalCompetencies: TotalCompetencies;

  constructor(competencies: Competency[] = []) {
    super();
    this.series = [];
    this.labels = [];
    this.competencyIDs = competencies.map((competency: Competency) => competency.competencyID);
    this.competencies = competencies;
    this.setTotalCompetencies();
  }

  setTotalCompetencies(): void {
    this.totalCompetencies = {};
    this.competencies.forEach((competency: Competency) => {
      const key: string = this.prefix + competency.competencyID;
      if (!(key in this.totalCompetencies)) {
        this.totalCompetencies[key] = {
          id: competency.competencyID,
          type: competency.competencyType,
          name: competency.name,
          scores: [],
        } as CompetencyWithScores;
      }
    });
  }

  /**
   * AIGROW2-184 箱ひげ図にデータテーブルを追加
   * 箱ひげ図のみにデータを表形式で表示する
   * EChartService が箱ひげ図以外のグラフでも共通なので、空配列を返すだけのものを追加しています
   * subTableColmuns
   * subTableData
   */
  get subTableColmuns(): any[] {
    return [];
  }
  get subTableData(): any[] {
    return [];
  }

  get options() {
    return {
      legend: {
        data: this.series.map((solo: any, index: number) => {
          return `SN-${index + 1}`;
        }),
        left: 'center',
      },
      xAxis: {
        type: 'value',
        name: i18n.t('STANDARD_DEVIATION').toString(),
        scale: true,
      },
      yAxis: {
        type: 'value',
        name: i18n.t('AVERAGE_VALUE').toString(),
        scale: true,
      },
      toolbox: {
        show: true,
        top: '50',
        right: 10,
        itemSize: 15,
        feature: {
          dataZoom: { show: true, title: { zoom: i18n.t('ENLARGE').toString(), back: i18n.t('UNDO').toString() } },
          restore: { show: true, title: i18n.t('RESET').toString() },
        },
      },
      tooltip: {
        formatter: function(params: any) {
          return params.name;
        },
      },
      series: this.series,
    };
  }

  addSeries(StatsCompetencyBoxplots: StatsCompetencyBoxplot[]): void {
    this.setTotalCompetencies();
    for (let row of StatsCompetencyBoxplots) {
      row.competencyScores.forEach((score: number, competencyID: number) => {
        // score 0 は対象外
        if (score > 0) {
          const key: string = this.prefix + competencyID;
          this.totalCompetencies[key].scores.push(score);
        }
      });
    }

    const result = [];
    for (let key in this.totalCompetencies) {
      // .scores の中身が空っぽの場合スキップする
      if (this.totalCompetencies[key].scores.length === 0) {
        continue;
      }
      // 平均値 Y軸
      const y = this.averageFromArray(this.totalCompetencies[key].scores);
      // 標準偏差 x軸
      const x = this.standardDeviationFromArray(this.totalCompetencies[key].scores, y);
      result.push({ pos: [x, y], name: this.totalCompetencies[key].name });
    }

    this.series.push({
      symbolSize: 10,
      name: `SN-${this.series.length + 1}`,
      data: result.map((d: any) => {
        return {
          name: d.name,
          value: d.pos,
          label: {
            show: true,
            // formatter: '{b}: {@label}',
            formatter: function(params: any) {
              return params.name;
            },
            // color: '#000000',
            // fontSize: 10,
            // fontWeight: 'bold',
            position: 'top',
            distance: 3,
          },
          tooltip: {
            formatter: function(params: any) {
              const line1: string = i18n.t('COMPETENCY_NAME').toString() + ` : ${params.name}`;
              const line2: string = i18n.t('AVERAGE').toString() + ` : ${NumberUtils.floor(params.value[1], 1000)}`;
              const line3: string = i18n.t('STANDARD_DEVIATION').toString() + ` : ${NumberUtils.floor(params.value[0], 1000)}`;
              return [line1, line2, line3].join('<br/>');
            },
          },
        };
      }),
      type: 'scatter',
    });
  }

  // 標準偏差
  standardDeviationFromArray(arr: number[], avg: number): number {
    let dist = 0;
    arr.forEach((x, i) => {
      dist += (x - avg) ** 2;
    });
    return Math.sqrt(dist / arr.length);
  }

  // 平均値
  averageFromArray(arr: number[]): number {
    let sum = 0;
    for (let i = 0; i < arr.length; i++) {
      sum += arr[i];
    }
    return sum / arr.length;
  }
}
